import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Chip, CircularProgress,
  Alert, Card, CardContent, Divider
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Room as RoomIcon,
  Person as PersonIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import { studentAPI } from '../../services/api';

const DAYS = [
  { id: 1, ar: 'الاثنين',   fr: 'Lundi'    },
  { id: 2, ar: 'الثلاثاء',  fr: 'Mardi'    },
  { id: 3, ar: 'الأربعاء',  fr: 'Mercredi' },
  { id: 4, ar: 'الخميس',   fr: 'Jeudi'    },
  { id: 5, ar: 'الجمعة',   fr: 'Vendredi' },
  { id: 6, ar: 'السبت',    fr: 'Samedi'   },
];

// Distinct colors for modules
const MODULE_COLORS = [
  '#3498db', '#e74c3c', '#2ecc71', '#f39c12', '#9b59b6',
  '#1abc9c', '#e67e22', '#34495e', '#c0392b', '#27ae60',
];

const StudentSchedule = () => {
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await studentAPI.getMySchedule();
        setSchedule(Array.isArray(data.schedule) ? data.schedule : []);
      } catch (err) {
        console.error(err);
        setError('تعذّر تحميل البرمجة الزمنية');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Assign a stable color per module code
  const moduleColorMap = {};
  [...new Set(schedule.map(s => s.module_code))].forEach((code, i) => {
    moduleColorMap[code] = MODULE_COLORS[i % MODULE_COLORS.length];
  });

  const sessionsByDay = (dayId) =>
    schedule
      .filter(s => Number(s.day_of_week) === dayId)
      .sort((a, b) => a.start_time.localeCompare(b.start_time));

  const fmt = (t) => (t ? t.slice(0, 5) : '');

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  }

  if (schedule.length === 0) {
    return (
      <Box textAlign="center" mt={8}>
        <ScheduleIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" fontFamily="Arabic UI Text, Arial">
          لا توجد برمجة زمنية متاحة حالياً
        </Typography>
        <Typography variant="body2" color="text.disabled" mt={1}>
          يرجى التواصل مع الإدارة لمزيد من المعلومات
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={2}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={1} mb={3}>
        <ScheduleIcon sx={{ color: '#3498db', fontSize: 32 }} />
        <Typography
          variant="h5"
          fontWeight="700"
          fontFamily="Arabic UI Text, Arial"
          sx={{ direction: 'rtl' }}
        >
          البرمجة الزمنية
        </Typography>
      </Box>

      {/* Summary chips */}
      <Box display="flex" flexWrap="wrap" gap={1} mb={3} justifyContent="flex-end">
        {Object.entries(moduleColorMap).map(([code, color]) => {
          const mod = schedule.find(s => s.module_code === code);
          return (
            <Chip
              key={code}
              label={mod?.module_name || code}
              size="small"
              sx={{
                bgcolor: color,
                color: 'white',
                fontFamily: 'Arabic UI Text, Arial',
                fontWeight: 600,
                fontSize: '0.72rem',
              }}
            />
          );
        })}
      </Box>

      {/* Days grid */}
      <Grid container spacing={2}>
        {DAYS.map((day) => {
          const sessions = sessionsByDay(day.id);
          return (
            <Grid item xs={12} sm={6} md={4} key={day.id}>
              <Paper
                elevation={sessions.length > 0 ? 3 : 1}
                sx={{
                  borderRadius: 3,
                  overflow: 'hidden',
                  opacity: sessions.length === 0 ? 0.5 : 1,
                }}
              >
                {/* Day header */}
                <Box
                  sx={{
                    background:
                      sessions.length > 0
                        ? 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)'
                        : '#bdc3c7',
                    py: 1.2,
                    px: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Chip
                    label={`${sessions.length} حصة`}
                    size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'white', fontSize: '0.7rem' }}
                  />
                  <Typography
                    variant="subtitle1"
                    fontWeight="700"
                    color="white"
                    fontFamily="Arabic UI Text, Arial"
                    sx={{ direction: 'rtl' }}
                  >
                    {day.ar}
                  </Typography>
                </Box>

                {/* Sessions */}
                <Box p={1.5} display="flex" flexDirection="column" gap={1.5}>
                  {sessions.length === 0 ? (
                    <Typography
                      variant="body2"
                      color="text.disabled"
                      textAlign="center"
                      fontFamily="Arabic UI Text, Arial"
                      py={1}
                    >
                      لا توجد حصص
                    </Typography>
                  ) : (
                    sessions.map((s) => (
                      <Card
                        key={s.id}
                        elevation={0}
                        sx={{
                          borderLeft: `4px solid ${moduleColorMap[s.module_code]}`,
                          bgcolor: `${moduleColorMap[s.module_code]}11`,
                          borderRadius: 2,
                        }}
                      >
                        <CardContent sx={{ p: '10px !important' }}>
                          {/* Module name */}
                          <Typography
                            variant="body2"
                            fontWeight="700"
                            sx={{ direction: 'rtl', fontFamily: 'Arabic UI Text, Arial', mb: 0.5 }}
                          >
                            {s.module_name || s.module_code}
                          </Typography>

                          <Divider sx={{ my: 0.5 }} />

                          {/* Time */}
                          <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                            <ScheduleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                              {fmt(s.start_time)} – {fmt(s.end_time)}
                            </Typography>
                          </Box>

                          {/* Location */}
                          {s.location && (
                            <Box display="flex" alignItems="center" gap={0.5} mt={0.3}>
                              <RoomIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {s.location}
                              </Typography>
                            </Box>
                          )}

                          {/* Professor */}
                          {s.professor_name && (
                            <Box display="flex" alignItems="center" gap={0.5} mt={0.3}>
                              <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {s.professor_name}
                              </Typography>
                            </Box>
                          )}

                          {/* Group */}
                          {s.group_name && s.group_name !== 'Tous' && (
                            <Box display="flex" alignItems="center" gap={0.5} mt={0.3}>
                              <GroupIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {s.group_name}
                              </Typography>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    ))
                  )}
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default StudentSchedule;
